import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, ViewChild } from '@angular/core';
import { WysiswygComponent } from '../../../../atoms/wysiswyg/wysiswyg.component';
import { AddDocumentComponent } from '../../../../atoms/add-document/add-document.component';
import { BriefTemplateDrawer } from '../../../../atoms/brief-template-drawer/brief-template-drawer';
import { CreatedCreativeBriefTemplate } from '../../../../services/creative-brief-template/creative-brief-template';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { BriefAiService } from '../../services/brief-ai.service';
import { BbField } from '../../ui/bb-field/bb-field';
import { BbEditList } from '../../ui/bb-edit-list/bb-edit-list';
import { BbTagInput } from '../../ui/bb-tag-input/bb-tag-input';
import { BbAiThoughtsScroll } from '../../ui/bb-ai-thoughts-scroll/bb-ai-thoughts-scroll';
import { ExternalBriefLinkModal } from './molecules/external-brief-link-modal/external-brief-link-modal';
import { briefOverviewRedirectsExternally } from './molecules/external-brief-link-modal/brief-overview-redirect';
import { isPublicGoogleDocUrl } from './molecules/google-doc-link/google-doc-link';

type CreativeMethod = 'upload' | 'type' | 'template' | 'ai';

/** Creative direction: concepts, ordered steps, do's/don'ts, and example-video pointers. */
@Component({
  selector: 'bb-creative-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BbField, BbEditList, BbTagInput, WysiswygComponent, BbAiThoughtsScroll, AddDocumentComponent, BriefTemplateDrawer, ExternalBriefLinkModal],
  templateUrl: './creative-section.html',
  styleUrls: ['./creative-section.scss'],
})
export class CreativeSection implements AfterViewInit {
  readonly service = inject(CreateBriefService);
  readonly briefAi = inject(BriefAiService);

  /** When true (wizard flow only), show the four-way method chooser above the editor. */
  readonly showMethodChooser = input<boolean>(false);

  @ViewChild('stepsWysiwyg') private stepsWysiwyg?: WysiswygComponent;
  @ViewChild('briefTemplateDrawer') private briefTemplateDrawer?: BriefTemplateDrawer;
  @ViewChild('externalBriefModal') private externalBriefModal?: ExternalBriefLinkModal;

  readonly dos = signal<string[]>([]);
  readonly donts = signal<string[]>([]);

  /** Optional free-text steer passed to the AI as the creative angle in the "Help Me" panel. */
  readonly aiSteer = signal<string>('');

  /** Draft text for a public Google Doc. Saved on the same URL as an uploaded file once it is valid. */
  readonly googleDocLink = signal('');
  readonly googleDocError = signal('');
  /** Bumped to remount the file dropzone after a file is removed. */
  readonly fileUploaderEpoch = signal(0);

  readonly hasUploadedFile = computed(() => {
    const url = this.service.uploadedBriefDocumentUrl().trim();
    return url.length > 0 && !isPublicGoogleDocUrl(url);
  });

  readonly showFileDropzone = computed(() => this.googleDocLink().trim().length === 0);
  readonly showGoogleDocField = computed(() => !this.hasUploadedFile());

  /** The four creative-direction input modes rendered by the chooser. */
  readonly methods: { id: CreativeMethod; title: string; desc: string; tag?: string }[] = [
    { id: 'upload', title: 'Document', desc: 'Bring an existing brief — PDF, DOCX, PPTX or Google Doc.' },
    { id: 'type', title: 'Type Requirements', desc: 'Write the concepts and shot list yourself.' },
    { id: 'template', title: 'Select Template', desc: 'Start from one of your saved templates.' },
    { id: 'ai', title: 'Help Me', desc: 'Let SHOUT AI draft it from your brief.', tag: 'AI' },
  ];

  /** The HTML we last wrote to the service, so the resync effect ignores our own edits. */
  private lastStepsHtml = '';
  /** Last overview HTML that does not point creators at an external brief. */
  private lastAcceptedStepsHtml = '';
  private rejectingOverview = false;
  private lastDoHtml = '';
  private lastDontHtml = '';

  constructor() {
    this.dos.set(this.seed(this.service.doPlainText(), this.service.doHtml()));
    this.donts.set(this.seed(this.service.dontPlainText(), this.service.dontHtml()));
    const initialSteps = this.service.stepsHtml();
    this.lastAcceptedStepsHtml = briefOverviewRedirectsExternally(initialSteps) ? '' : initialSteps;
    this.lastStepsHtml = initialSteps;
    this.lastDoHtml = this.service.doHtml();
    this.lastDontHtml = this.service.dontHtml();

    effect(() => {
      const stepsHtml = this.service.stepsHtml();
      const doHtml = this.service.doHtml();
      const dontHtml = this.service.dontHtml();
      if (stepsHtml !== this.lastStepsHtml) {
        this.lastStepsHtml = stepsHtml;
        if (!this.showMethodChooser() || !briefOverviewRedirectsExternally(stepsHtml)) {
          this.lastAcceptedStepsHtml = stepsHtml;
        }
        this.stepsWysiwyg?.applyExternalHtml(stepsHtml);
      }
      if (doHtml !== this.lastDoHtml) {
        this.lastDoHtml = doHtml;
        this.dos.set(this.seed(this.service.doPlainText(), doHtml));
      }
      if (dontHtml !== this.lastDontHtml) {
        this.lastDontHtml = dontHtml;
        this.donts.set(this.seed(this.service.dontPlainText(), dontHtml));
      }
    });

    effect(() => {
      const url = this.service.uploadedBriefDocumentUrl().trim();
      if (isPublicGoogleDocUrl(url)) {
        this.googleDocLink.set(url);
      }
    });
  }

  ngAfterViewInit(): void {
    const stepsHtml = this.service.stepsHtml();
    if (stepsHtml.trim().length > 0) {
      queueMicrotask(() => this.stepsWysiwyg?.applyExternalHtml(stepsHtml));
    }
  }

  onStepsHtmlChange(html: string): void {
    if (this.rejectingOverview || html === this.lastStepsHtml) {
      return;
    }
    if (this.showMethodChooser() && briefOverviewRedirectsExternally(html)) {
      this.rejectOverview();
      return;
    }
    this.lastAcceptedStepsHtml = html;
    this.lastStepsHtml = html;
    this.service.stepsHtml.set(html);
  }

  private rejectOverview(): void {
    const restore = this.lastAcceptedStepsHtml;
    this.rejectingOverview = true;
    this.lastStepsHtml = restore;
    this.stepsWysiwyg?.applyExternalHtml(restore);
    if (this.service.stepsHtml() !== restore) {
      this.service.stepsHtml.set(restore);
    }
    this.externalBriefModal?.open();
    this.rejectingOverview = false;
  }

  private seed(plain: string, html: string): string[] {
    if (plain && plain.trim()) {
      return plain.split('\n').filter((line) => line.trim().length > 0);
    }
    if (!html) {
      return [];
    }
    const listItems = [...html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
      .map((match) => this.stripTags(match[1]))
      .filter((line) => line.length > 0);
    if (listItems.length) {
      return listItems;
    }
    const text = this.stripTags(html);
    return text ? [text] : [];
  }

  private stripTags(value: string): string {
    return value.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  }

  private escape(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private toList(items: string[], ordered: boolean): string {
    const clean = items.filter((item) => item.trim().length > 0);
    if (!clean.length) {
      return '';
    }
    const tag = ordered ? 'ol' : 'ul';
    return `<${tag}>${clean.map((item) => `<li>${this.escape(item)}</li>`).join('')}</${tag}>`;
  }

  setDos(items: string[]): void {
    this.dos.set(items);
    this.service.doPlainText.set(items.filter((i) => i.trim()).join('\n'));
    this.lastDoHtml = this.toList(items, false);
    this.service.doHtml.set(this.lastDoHtml);
  }

  setDonts(items: string[]): void {
    this.donts.set(items);
    this.service.dontPlainText.set(items.filter((i) => i.trim()).join('\n'));
    this.lastDontHtml = this.toList(items, false);
    this.service.dontHtml.set(this.lastDontHtml);
  }

  addConcept(): void {
    this.service.creativeConcepts.update((list) => [...list, { id: list.length + 1, name: '', description: '' }]);
  }

  removeConcept(index: number): void {
    this.service.creativeConcepts.update((list) => list.filter((_, i) => i !== index));
  }

  updateConcept(index: number, key: 'name' | 'description', value: string): void {
    this.service.creativeConcepts.update((list) =>
      list.map((concept, i) => (i === index ? { ...concept, [key]: value } : concept)),
    );
  }

  /** Switch the active input mode; opening the template drawer immediately for the template mode. */
  selectMethod(id: CreativeMethod): void {
    this.service.creativeMethod.set(id);
    if (id === 'template') {
      queueMicrotask(() => this.briefTemplateDrawer?.openDrawer());
    }
  }

  /** Drop the user into the Type editor (used after a template/AI apply, and by the banner "Change" action). */
  goType(): void {
    this.service.creativeMethod.set('type');
  }

  /** Upload mode: store the uploaded document URL (submitted as briefDocumentUrl). */
  onDocumentUploaded(event: { file: string; fileKey: string; fullUrl: string }): void {
    this.googleDocLink.set('');
    this.googleDocError.set('');
    this.service.uploadedBriefDocumentUrl.set(event.fullUrl);
  }

  onGoogleDocLinkInput(value: string): void {
    this.googleDocLink.set(value);
    const trimmed = value.trim();
    if (!trimmed) {
      this.googleDocError.set('');
      this.clearSavedGoogleDoc();
      return;
    }
    if (isPublicGoogleDocUrl(trimmed)) {
      this.googleDocError.set('');
      this.service.uploadedBriefDocumentUrl.set(trimmed);
      return;
    }
    this.clearSavedGoogleDoc();
  }

  onGoogleDocLinkBlur(): void {
    const trimmed = this.googleDocLink().trim();
    if (!trimmed || isPublicGoogleDocUrl(trimmed)) {
      this.googleDocError.set('');
      return;
    }
    this.googleDocError.set('Enter a public Google Doc link, like https://docs.google.com/document/d/…');
  }

  /** Drop the Google Doc so a file can be uploaded instead. */
  clearGoogleDocLink(): void {
    this.googleDocLink.set('');
    this.googleDocError.set('');
    this.clearSavedGoogleDoc();
  }

  /** Drop the uploaded file so a Google Doc link can be used instead. */
  clearUploadedDocument(): void {
    this.service.uploadedBriefDocumentUrl.set('');
    this.fileUploaderEpoch.update((epoch) => epoch + 1);
  }

  private clearSavedGoogleDoc(): void {
    if (isPublicGoogleDocUrl(this.service.uploadedBriefDocumentUrl())) {
      this.service.uploadedBriefDocumentUrl.set('');
    }
  }

  /**
   * Apply a selected/created template into the creative fields, then land in the Type editor.
   * The section's resync effect re-hydrates the WYSIWYG and do/don't lists from these signals,
   * so no imperative applyExternalHtml is needed here.
   */
  onBriefTemplateApplied(created: CreatedCreativeBriefTemplate): void {
    const stepsHtml =
      (created.stepsHtml?.trim().length ? created.stepsHtml : created.creativeBriefContent) ?? '';
    this.service.stepsHtml.set(stepsHtml);
    this.service.description.set(stepsHtml);
    this.service.doHtml.set(created.doHtml ?? '');
    this.service.dontHtml.set(created.dontHtml ?? '');
    this.service.stepsPlainText.set(created.stepsPlainText ?? '');
    this.service.doPlainText.set(created.doPlainText ?? '');
    this.service.dontPlainText.set(created.dontPlainText ?? '');
    if (Array.isArray(created.creativeConcepts) && created.creativeConcepts.length) {
      this.service.creativeConcepts.set(created.creativeConcepts);
    }
    this.service.creativeTemplateApplied.set(true);
    this.goType();
  }

  /** Help Me mode: stream a full AI draft from the brief, then land in the Type editor on success. */
  async draftWithAi(): Promise<void> {
    const angle = this.aiSteer().trim() || 'Create a strong starter brief considering the product and campaign.';
    const success = await this.briefAi.draftBrief(angle);
    if (success) {
      this.service.creativeTemplateApplied.set(false);
      this.goType();
    }
  }

  readonly checkIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            class="size-4 text-green-500 dark:text-green-400">
            <path d="M20 6 9 17l-5-5" />
        </svg>`;

  readonly crossIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" 
  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" 
  class="size-4 text-red-500 dark:text-red-400">
  <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
  </svg>`
}
